import { usePayerBanks } from '@modules/Wallets/BankAccounts/hooks/usePayerBanks';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Input } from '@shared/components/ui/input';
import Tooltip from '@shared/components/ui/tooltip/tooltip';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatGenerationIdInput, isCompleteGenerationId } from '../../lib/generationIdInput';
import type { WithdrawalPaymentFilesFilters } from '../../models';

interface PaymentFilesFiltersSectionProps {
  filters: WithdrawalPaymentFilesFilters;
  onFiltersChange: (filters: Partial<WithdrawalPaymentFilesFilters>) => void;
}

const formatDateParam = (date?: Date) => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);

export const PaymentFilesFiltersSection = ({ filters, onFiltersChange }: PaymentFilesFiltersSectionProps) => {
  const { t } = useTranslation('withdrawalPaymentFilesModule');

  const [generationIdInput, setGenerationIdInput] = useState(filters.generation_id ?? '');
  const [generationIdFocused, setGenerationIdFocused] = useState(false);

  useEffect(() => {
    setGenerationIdInput(filters.generation_id ?? '');
  }, [filters.generation_id]);

  const { data: payerBanksData, isLoading: payerBanksLoading } = usePayerBanks();

  const payerBankOptions = useMemo(
    () =>
      (payerBanksData?.data ?? []).map((bank) => ({
        label: bank.name,
        value: String(bank.id),
      })),
    [payerBanksData?.data],
  );

  const dateFrom = filters.date_from ? new Date(filters.date_from) : undefined;
  const dateTo = filters.date_to ? new Date(filters.date_to) : undefined;

  const isGenerationIdIncomplete = generationIdInput.length > 0 && !isCompleteGenerationId(generationIdInput);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.payer_bank_id) count++;
    if (filters.generation_id) count++;
    if (filters.date_from || filters.date_to) count++;
    return count;
  }, [filters.payer_bank_id, filters.generation_id, filters.date_from, filters.date_to]);

  const handleGenerationIdChange = (raw: string) => {
    const formatted = formatGenerationIdInput(raw);
    setGenerationIdInput(formatted);

    if (!formatted) {
      onFiltersChange({ generation_id: undefined });
      return;
    }

    if (isCompleteGenerationId(formatted)) {
      onFiltersChange({ generation_id: formatted });
    } else {
      onFiltersChange({ generation_id: undefined });
    }
  };

  const handleClearFilters = () => {
    setGenerationIdInput('');
    onFiltersChange({ payer_bank_id: undefined, generation_id: undefined, date_from: undefined, date_to: undefined });
  };

  return (
    <FiltersDropdown
      title={t('filters.title', 'Filtros')}
      activeFiltersCount={activeFiltersCount}
      triggerTestId="payment-files-filters-toggle-button"
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.payer_bank.label', 'Banco pagador')}>
            <Combobox
              options={payerBankOptions}
              value={filters.payer_bank_id ? String(filters.payer_bank_id) : ''}
              setValue={(value) => onFiltersChange({ payer_bank_id: value ? Number(value) : undefined })}
              placeholder={payerBanksLoading ? t('filters.payer_bank.loading') : t('filters.payer_bank.placeholder')}
              dataTestId="payment-files-payer-bank-filter"
            />
          </FilterField>

          <FilterField label={t('filters.generation_id.label', 'ID de generación')}>
            <Tooltip
              disabled={!isGenerationIdIncomplete}
              open={isGenerationIdIncomplete && generationIdFocused ? true : undefined}
              content={<span className="text-xs text-warning">{t('filters.generation_id.incomplete')}</span>}
              trigger={
                <Input
                  value={generationIdInput}
                  onChange={(e) => handleGenerationIdChange(e.target.value)}
                  onFocus={() => setGenerationIdFocused(true)}
                  onBlur={() => setGenerationIdFocused(false)}
                  placeholder={t('filters.generation_id.placeholder')}
                  aria-label={t('filters.generation_id.placeholder')}
                  aria-invalid={isGenerationIdIncomplete}
                  maxLength={36}
                  inputMode="text"
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-sm"
                  data-testid="payment-files-generation-id-filter"
                />
              }
              side="bottom"
              align="start"
              data-testid="payment-files-generation-id-hint-tooltip"
            />
          </FilterField>

          <FilterField label={t('filters.dates.label', 'Fecha de generación')}>
            <DateRangePicker
              initialDateFrom={dateFrom}
              initialDateTo={dateTo}
              onUpdate={({ range }) =>
                onFiltersChange({
                  date_from: formatDateParam(range.from),
                  date_to: formatDateParam(range.to),
                })
              }
              placeholder={t('filters.dates.placeholder')}
              dataTestId="payment-files-date-filter"
              triggerClassName="h-[38px] text-sm w-full"
            />
          </FilterField>
        </div>

        {activeFiltersCount > 0 && (
          <div className="flex items-center justify-end pt-3 border-t border-card-border">
            <Button
              variant="ghost"
              size="sm"
              className="text-sm text-muted-foreground hover:text-foreground"
              onClick={handleClearFilters}
              type="button"
            >
              Limpiar {activeFiltersCount} {activeFiltersCount === 1 ? 'filtro' : 'filtros'}
            </Button>
          </div>
        )}
      </div>
    </FiltersDropdown>
  );
};
